import type { Planungsfrage } from './types';
import { datumDeutsch } from './unterlagenLogic';

/**
 * Fristgruppen beim Stellen von Fragen.
 *
 * Eine A-Frage hat im Katalog 14 Tage Rueckantwortfrist, eine B- oder
 * C-Frage 21. Wer eine gemischte Auswahl mit EINER Frist stempelt, gibt der
 * haftungsrelevanten Frage stillschweigend eine Woche mehr — oder setzt die
 * unkritische unnoetig unter Druck. Deshalb wird die Auswahl hier nach der
 * Katalogfrist aufgeteilt und je Gruppe mit eigener Frist geschrieben.
 *
 * Welche Frist zu einem Code gehoert, entscheidet `fristTageFuerCode` in
 * `planungLogic.ts`; sie wird hereingereicht, damit die Aufteilung ohne den
 * Katalog pruefbar bleibt.
 */

export interface FristGruppe {
    /** Rueckantwortfrist in Tagen ab Stelldatum. */
    tage: number;
    ids: string[];
    codes: string[];
}

/** Was eine Gruppe nach dem Schreiben zurueckmeldet. */
export interface FristErgebnis {
    tage: number;
    /** ISO-Datum (YYYY-MM-DD) der gesetzten Frist. */
    fristAm: string;
    /** Fragen in der Gruppe. */
    gesamt: number;
    /** Davon tatsaechlich neu gestempelt — bei den uebrigen stand schon ein
     *  Stelldatum, das bleibt (Guard im UPDATE). */
    gestempelt: number;
}

/**
 * Teilt Fragen nach ihrer Frist auf. Gruppen aufsteigend nach Tagen, die
 * Fragen innerhalb einer Gruppe in Eingabereihenfolge. Eine Frist, die keine
 * positive Zahl ist, wird auf 21 Tage gezogen — lieber die laengere Standard-
 * frist als gar keine, denn eine gestellte Frage ohne Frist faellt aus jeder
 * Ueberfaelligkeitspruefung heraus.
 */
export const gruppiereNachFrist = (
    fragen: Pick<Planungsfrage, 'id' | 'code'>[],
    fristFuer: (code: string) => number,
): FristGruppe[] => {
    const nachTagen = new Map<number, FristGruppe>();
    (fragen || []).forEach(f => {
        if (!f || !f.id) return;
        const roh = fristFuer(f.code);
        const tage = Number.isFinite(roh) && roh > 0 ? Math.round(roh) : 21;
        const gruppe = nachTagen.get(tage) || { tage, ids: [], codes: [] };
        if (!gruppe.ids.includes(f.id)) {
            gruppe.ids.push(f.id);
            gruppe.codes.push(f.code);
        }
        nachTagen.set(tage, gruppe);
    });
    return Array.from(nachTagen.values()).sort((a, b) => a.tage - b.tage);
};

const fragenWort = (n: number): string => (n === 1 ? 'Frage' : 'Fragen');

/**
 * Erfolgsmeldung nach dem Stellen. Nennt jede gesetzte Frist mit Datum, und
 * — wenn nicht alle Zeilen neu gestempelt wurden — den Hinweis, dass das
 * ERSTE Stelldatum stehen bleibt. Dieser Hinweis ist kein Schoenheitsdetail:
 * fuer die Fristberechnung und im Haftungsfall zaehlt, wann zum ersten Mal
 * gefragt wurde.
 */
export const stellMeldung = (ergebnisse: FristErgebnis[]): string => {
    const liste = ergebnisse || [];
    const gesamt = liste.reduce((s, e) => s + e.gesamt, 0);
    const gestempelt = liste.reduce((s, e) => s + e.gestempelt, 0);
    if (!gesamt) return 'Keine Frage ausgewählt.';

    const fristen = liste
        .map(e => {
            const datum = datumDeutsch(e.fristAm) || 'ohne Datum';
            const anteil = e.gestempelt === e.gesamt
                ? `${e.gesamt} ${fragenWort(e.gesamt)}`
                : `${e.gestempelt} von ${e.gesamt} ${fragenWort(e.gesamt)}`;
            return `${e.tage} Tage bis ${datum} (${anteil})`;
        })
        .join('; ');

    if (gestempelt === gesamt) {
        return `${gesamt} ${fragenWort(gesamt)} als gestellt festgehalten. Frist: ${fristen}.`;
    }
    return `${gestempelt} von ${gesamt} ${fragenWort(gesamt)} neu gestempelt. Frist: ${fristen}. `
        + 'Bei den übrigen blieb das ERSTE Stelldatum stehen — dafür zählt, wann zum ersten Mal gefragt wurde.';
};
